import { AxiosResponse } from 'axios';
import { runInAction } from 'mobx';

import { notUndefined, UnreachableFunction } from 'ui/base/types';

import { Optional } from 'services/base/types';
import { getResponseBytes, http } from 'services/base/http';

import {
  IPangolinDomain,

  UpdateHistory,
  UpdateHistoryMap,
  UpdateHistoryRefMap,
  UpdateHistoryType,

  UpdateRestoreMap,
  UpdateUpgradeMap,
} from './types';

import {
  UpdateHistoryObject as UpdateHistoryProto,
  UpdateHistoryType as UpdateHistoryTypeProto,

  GetUpdateHistoryResponse,
} from './protos/update_pb';

import { upgradeFromProto } from './update_upgrade_service';
import { restoreFromProto } from './update_restore_service';

export class UpdateHistoryService {
  static async fetchAll(domain: IPangolinDomain, save = true): Promise<UpdateHistoryMap> {
    const httpResp = await http.get(`/pangolin/${domain.id}/update/history`);
    const remoteHistory = parseUpdateHistoryResponse(httpResp);
    return runInAction(() => {
      if (save) {
        const remoteHistoryRef = historyFromHistory(remoteHistory);
        const remoteUpgrades = upgradesFromHistory(remoteHistory);
        const remoteRestores = restoresFromHistory(remoteHistory);
        domain.updateUpgrades = remoteUpgrades;
        domain.updateRestores = remoteRestores;
        domain.updateHistory = remoteHistoryRef;
      }
      return remoteHistory;
    });
  }
}

function historyFromHistory(history: UpdateHistoryMap): UpdateHistoryRefMap {
  return new UpdateHistoryRefMap(history.values.map(h => {
    switch (h.type) {
      case UpdateHistoryType.UPGRADE: return [h.upgrade.id, { type: UpdateHistoryType.UPGRADE, upgrade_id: h.upgrade.id }];
      case UpdateHistoryType.RESTORE: return [h.restore.id, { type: UpdateHistoryType.RESTORE, restore_id: h.restore.id }];
      default: return UnreachableFunction(h);
    }
  }));
}

function upgradesFromHistory(history: UpdateHistoryMap): UpdateUpgradeMap {
  const upgrades = history.values.map((h) => h.type === UpdateHistoryType.UPGRADE ? h.upgrade : undefined);
  return new UpdateUpgradeMap(upgrades.filter(notUndefined).map((h) => [h.id, h]));
}

function restoresFromHistory(history: UpdateHistoryMap): UpdateRestoreMap {
  const restores = history.values.map((h) => h.type === UpdateHistoryType.RESTORE ? h.restore : undefined);
  return new UpdateRestoreMap(restores.filter(notUndefined).map((h) => [h.id, h]));
}

function parseUpdateHistoryResponse(httpResp: AxiosResponse): UpdateHistoryMap {
  const rawBytes = getResponseBytes(httpResp);
  const rootProto = GetUpdateHistoryResponse.deserializeBinary(rawBytes);
  const listProto = rootProto.getHistoryList();
  const historyObjects = listProto.map(historyFromProto).filter(notUndefined);
  return new UpdateHistoryMap(historyObjects.map((h) => {
    switch (h.type) {
      case UpdateHistoryType.UPGRADE: return [h.upgrade.id, h];
      case UpdateHistoryType.RESTORE: return [h.restore.id, h];
      default: return UnreachableFunction(h);
    }
  }));
}

function historyFromProto(proto: Optional<UpdateHistoryProto>): Optional<UpdateHistory> {
  if (proto == null) return undefined;

  const type = proto.getType();

  if (type === undefined) return undefined;

  switch (type) {
    case UpdateHistoryTypeProto.UPDATE_HISTORY_TYPE_UPGRADE: {
      const upgradeProto = proto.getUpgrade();
      if (upgradeProto == null) return undefined;
      const upgrade = upgradeFromProto(upgradeProto);
      if (upgrade == null) return undefined;
      return { type: UpdateHistoryType.UPGRADE, upgrade };
    }
    case UpdateHistoryTypeProto.UPDATE_HISTORY_TYPE_RESTORE: {
      const restoreProto = proto.getRestore();
      if (restoreProto == null) return undefined;
      const restore = restoreFromProto(restoreProto);
      if (restore == null) return undefined;
      return { type: UpdateHistoryType.RESTORE, restore };
    }
    default: {
      return UnreachableFunction(type);
    }
  }
}
